// Brain map health overlay (Admin > Brain map): per module, the last write time of its tables and the app_errors of the
// last 7 days. Pure functions: the Admin page gets the raw numbers from /api/admin?resource=brain_health, maps them onto
// the modules of the map data (G, the JSON island of docs/brain-map.html) and posts the result into the map frame.

// the map data inside the served HTML
export function mapData(html) {
  const m = String(html || '').match(/<script id="data" type="application\/json">([\s\S]*?)<\/script>/)
  if (!m) return null
  try { return JSON.parse(m[1]) } catch (e) { return null }
}

// which module an error belongs to:
//  - database links (source 'db'): "<trigger> <what>: <message>" -> the trigger's table -> its module
//  - anything else: the first table of the map named in the message or url -> its module
//  - otherwise the System module
export function errorModule(G, err) {
  const tableMod = {}
  for (const n of G.nodes || []) if (n.kind === 'table') tableMod[n.label] = n.mod
  const trig = G.triggers || {}
  const text = String(err.message || '')
  if (err.source === 'db') {
    const t = trig[text.split(/[\s:]/)[0]]
    if (t && tableMod[t]) return tableMod[t]
  }
  const words = (text + ' ' + String(err.url || '')).match(/\b[a-z][a-z0-9_]{2,}\b/g) || []
  for (const w of words) if (tableMod[w]) return tableMod[w]
  return (G.modules || []).some(m => m.key === 'sys') ? 'sys' : null
}

// { [table]: 0..1 } for the 3D map's sparks (busy = more / faster, 0 = idle, no sparks). health.tables holds the last
// write per table (hc_brain_health()): written in the last hour = 1, fading to a trickle at 7 days, older = 0 (not listed).
// A number there instead of a time is read as the count of writes in the window (1000+ = 1).
export function tableActivity(health, now = Date.now(), days = 7) {
  const out = {}, hours = days * 24
  for (const [t, v] of Object.entries((health && health.tables) || {})) {
    if (typeof v === 'number') { if (v > 0) out[t] = Math.round(Math.min(1, Math.log10(1 + v) / 3) * 1000) / 1000; continue }
    const ts = Date.parse(v); if (!ts) continue
    const h = Math.max(0, (now - ts) / 36e5); if (h > hours) continue
    out[t] = Math.round(Math.max(0.08, 1 - Math.log(1 + h) / Math.log(1 + hours)) * 1000) / 1000
  }
  return out
}

// { [module]: { last_write, last_table, errors, occurrences, samples } }
// errors = distinct errors seen in the window; occurrences = their counters (app_errors.count is a running total)
export function moduleHealth(G, health, now = Date.now(), days = 7) {
  const out = {}
  const tableMod = {}
  for (const n of G.nodes || []) if (n.kind === 'table') tableMod[n.label] = n.mod
  for (const m of G.modules || []) out[m.key] = { last_write: null, last_table: null, errors: 0, occurrences: 0, samples: [] }
  for (const [t, ts] of Object.entries((health && health.tables) || {})) {
    const m = tableMod[t]; if (!m || !out[m] || !ts) continue
    if (!out[m].last_write || Date.parse(ts) > Date.parse(out[m].last_write)) { out[m].last_write = ts; out[m].last_table = t }
  }
  const since = now - days * 864e5
  for (const e of (health && health.errors) || []) {
    if (e.last_seen && Date.parse(e.last_seen) < since) continue
    const m = errorModule(G, e); if (!m || !out[m]) continue
    out[m].errors++; out[m].occurrences += Number(e.count) || 1
    if (out[m].samples.length < 5) out[m].samples.push({ message: String(e.message || '').slice(0, 200), source: e.source || '', count: Number(e.count) || 1, last_seen: e.last_seen || null })
  }
  return out
}
